/**
 * Auth gate (R7.1). The SPA must authenticate through Cognito before an
 * interview can start or resume. The Cognito integration is behind
 * AuthProvider so the gate is unit-testable; the real provider
 * (cognito-auth.ts) runs the hosted sign-in with authorization code + PKCE.
 * The gate exposes the access token used to open the agent WebSocket.
 */
export interface AuthSession {
  /** Cognito access token: what the AgentCore Runtime JWT authorizer admits. */
  accessToken: string;
  cognitoSub: string;
  /** Display name (email) from the ID token, when available. */
  username?: string;
  /** Access-token expiry, epoch ms. */
  expiresAt: number;
}

export interface AuthProvider {
  getSession(): Promise<AuthSession | null>;
  signIn(): Promise<AuthSession>;
}

/** Treat a token as expired this long before it actually expires. */
const EXPIRY_SKEW_MS = 60_000;

export class AuthGate {
  private session: AuthSession | null = null;

  constructor(
    private readonly provider: AuthProvider,
    private readonly now: () => number = () => Date.now(),
  ) {}

  private valid(s: AuthSession | null): s is AuthSession {
    return !!s && s.expiresAt - EXPIRY_SKEW_MS > this.now();
  }

  /** Returns a valid session, signing in if necessary. */
  async requireSession(): Promise<AuthSession> {
    if (this.valid(this.session)) return this.session;
    const existing = await this.provider.getSession();
    if (this.valid(existing)) {
      this.session = existing;
      return existing;
    }
    this.session = await this.provider.signIn();
    return this.session;
  }

  isAuthenticated(): boolean {
    return this.valid(this.session);
  }

  current(): AuthSession | null {
    return this.session;
  }
}
